/**
 * The thread header: identity, state, assignment, and one overflow.
 *
 * ┌─ ⛔ THE CONTRACT IT KEEPS IS "A NEW THING GETS A SLOT OR DOES NOT BELONG" ──┐
 * │ `header-view.ts` carries the full reasoning. The short version: this bar    │
 * │ sits above a message list an agent reads all day, and the scope document    │
 * │ already promises it two MORE things (the 24-hour window countdown and       │
 * │ presence) that are in none of items 14, 15 or 16. Three visible buttons     │
 * │ becomes six. **So state is the control** - clicking the status opens the    │
 * │ transitions, clicking the assignee opens the picker - and nothing is added  │
 * │ beside them.                                                                │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * ⛔ IT RENDERS WITH NOTHING OPEN, as an empty bar. Same argument as the panel
 * pane existing when no conversation is selected: unmounting it would move the
 * message list up and down by the header's height on every click, which reads
 * as a rendering fault and is a thing only a browser can see.
 */
import { useEffect, useRef, useState } from 'react'
import type { ConversationPanel } from '../../../src/services/conversation-panel.js'
import {
  type Status,
  headerAssignee,
  headerIdentity,
  stateRender,
  transitionsFrom,
} from './header-view.js'
import {
  type AssignableUser,
  type SnoozePreset,
  ConversationGone,
  cancelSnooze,
  fetchAssignableUsers,
  setAssignee,
  setSnooze,
  setStatus,
} from '../data/inbox-api.js'

/** The words for each status. The module decides which applies; this says it. */
const STATUS_WORD: Record<Status, string> = {
  open: 'Open',
  answered: 'Answered',
  closed: 'Closed',
}

/**
 * ⛔ THE PRESETS ARE THE VERSION THAT CAN BE BUILT, NOT THE SMALL VERSION OF A
 * PICKER. A calendar saying "3pm" must know whose 3pm, and every timezone-aware
 * formatting API is forbidden outside `src/time/display-timezone.ts`. These are
 * computed server-side from `now()` in Postgres, so no client ever formats a
 * business-local time. See `services/snooze.ts`.
 */
const PRESETS: ReadonlyArray<{ preset: SnoozePreset; label: string }> = [
  { preset: 'in_1_hour', label: 'In 1 Hour' },
  { preset: 'today', label: 'Later Today' },
  { preset: 'tomorrow', label: 'Tomorrow' },
  { preset: 'in_7_days', label: 'In 7 Days' },
  { preset: 'in_30_days', label: 'In 30 Days' },
]

export type ThreadHeaderProps = {
  /** `undefined` = nothing open. `null` = asked, and there is nothing. */
  panel: ConversationPanel | null | undefined
  conversationId: string | null
  /** Run after any successful write. The shell refetches list, thread and panel. */
  onChanged?: () => void | Promise<void>
  /** TEST SEAM, exactly like `InboxShell`'s socket. Production passes nothing. */
  loadUsers?: typeof fetchAssignableUsers
  /**
   * ⛔ A MENU THE COMMAND PALETTE ASKED FOR. The palette does not implement
   * snooze or assign - it asks the header to open the control it already owns,
   * so there is ONE implementation, one set of words and one failure line.
   * Cleared through `onMenuOpened` as soon as it is honoured, so reopening the
   * palette and pressing Escape does not leave a request pending.
   */
  requestedMenu?: 'snooze' | 'assign' | null
  onMenuOpened?: () => void
}

type MenuKind = 'status' | 'assignee' | 'snooze' | null

export function ThreadHeader({
  panel,
  conversationId,
  onChanged,
  loadUsers = fetchAssignableUsers,
  requestedMenu = null,
  onMenuOpened,
}: ThreadHeaderProps): React.JSX.Element {
  const [menu, setMenu] = useState<MenuKind>(null)
  const [users, setUsers] = useState<AssignableUser[] | null>(null)
  const [busy, setBusy] = useState(false)
  /**
   * ⛔ A WRITE FAILURE HAS TO BE SAID, and the header is the only place that can
   * say it. Every control here changes a fact somebody will act on; a status
   * change that silently did not happen leaves an agent believing a conversation
   * is closed. Third state, as everywhere: `null` is "nothing has failed", not
   * "it worked".
   */
  const [failure, setFailure] = useState<string | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  // Any conversation change closes whatever was open. Without it, a menu for A
  // stays on screen over B's header and its next click writes to B.
  useEffect(() => {
    setMenu(null)
    setFailure(null)
  }, [conversationId])

  /**
   * ⚠️ THE PALETTE'S REQUEST IS HONOURED AND THEN CLEARED IN THE SAME EFFECT.
   * Left set, closing the menu by hand would immediately reopen it, because the
   * request would still be the current props value.
   */
  useEffect(() => {
    if (requestedMenu === null) return
    /**
     * ⚠️ THE PALETTE'S VOCABULARY IS NOT THIS COMPONENT'S, AND THE MAPPING IS
     * EXPLICIT. The palette says `assign` because that is the verb a person
     * types; the header's slot is `assignee` because that is what it shows.
     * Passing one through as the other compiled to nothing and was caught by
     * `tsc` - the two vocabularies are allowed to differ, and a translation
     * that is written down is the reason they can.
     */
    setMenu(requestedMenu === 'assign' ? 'assignee' : 'snooze')
    onMenuOpened?.()
  }, [requestedMenu, onMenuOpened])

  /**
   * ⚠️ LOADED WHEN THE PICKER OPENS, NOT ON MOUNT. Staff do not change while
   * somebody reads a thread, and fetching a list nobody has asked for on every
   * conversation open is a request per click for a menu most clicks never open.
   */
  useEffect(() => {
    if (menu !== 'assignee' || users !== null) return
    let live = true
    void loadUsers()
      .then((res) => {
        if (live) setUsers(res.users)
      })
      .catch(() => {
        if (live) setFailure('Could not load the list of people')
      })
    return () => {
      live = false
    }
  }, [menu, users, loadUsers])

  // Clicking away closes the menu. `mousedown` rather than `click`, so it
  // closes before the element under the pointer handles its own click.
  useEffect(() => {
    if (menu === null) return
    const away = (e: MouseEvent): void => {
      if (!rootRef.current?.contains(e.target as Node)) setMenu(null)
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [menu])

  if (conversationId === null || panel === undefined) {
    // The empty bar. It holds the height and says nothing.
    return <div className="thread-header thread-header--empty" data-testid="thread-header-none" />
  }

  if (panel === null) {
    return (
      <div className="thread-header thread-header--empty" data-testid="thread-header-unknown">
        <span className="thread-header__title">Conversation unavailable</span>
      </div>
    )
  }

  const { identity } = panel
  const who = headerIdentity(identity)
  const state = stateRender(identity)
  const assignee = headerAssignee(identity)

  /**
   * ⛔ EVERY WRITE GOES THROUGH HERE so the three outcomes cannot be handled
   * three different ways. `ConversationGone` is its own sentence: the row moved
   * out of this agent's reach or was removed while they were looking at it, and
   * "not found" said as a network error sends somebody to the wrong place.
   */
  const run = async (what: () => Promise<unknown>): Promise<void> => {
    setBusy(true)
    setFailure(null)
    try {
      await what()
      setMenu(null)
      await onChanged?.()
    } catch (err) {
      setFailure(
        err instanceof ConversationGone
          ? 'This conversation is no longer available to you'
          : 'That did not go through. Nothing was changed.',
      )
    } finally {
      setBusy(false)
    }
  }

  const toggle = (kind: Exclude<MenuKind, null>): void => {
    setMenu((open) => (open === kind ? null : kind))
  }

  /**
   * ⚠️ NO TRANSITION IS TWO-STEP ANY MORE. Until 2026-08-20 close staged a
   * pending confirm, because closing wrote a line the customer could see. Amir
   * reversed that, so `confirmsTransition` returns false for everything and the
   * staging branch is gone rather than kept behind a condition that cannot be
   * true. `header-view.ts` holds the reasoning and the one line to revive.
   */
  const chooseStatus = (to: Status): void => {
    void run(() => setStatus(conversationId, to))
  }

  return (
    <div className="thread-header" data-testid="thread-header" ref={rootRef}>
      <div className="thread-header__identity">
        <span className="thread-header__title" data-testid="header-title">
          {/* The word for "we have neither a name nor a number" is this
              component's, per `headerIdentity`. */}
          {who.title ?? 'Unknown Contact'}
        </span>
        <span className="thread-header__channel">{who.channel}</span>
      </div>

      {/*
        ⛔ THE STATE SLOT IS ONE CONTROL FOR TWO FACTS, because status and snooze
        are one reading - where this conversation sits. They are separate
        columns and must not be flattened into one word: a snoozed conversation
        is still open or answered, and that matters when it wakes.
      */}
      <div className="thread-header__slot">
        <button
          type="button"
          className="thread-header__state"
          data-testid="header-status"
          aria-haspopup="menu"
          aria-expanded={menu === 'status'}
          disabled={busy}
          onClick={() => toggle('status')}
        >
          {STATUS_WORD[state.status]}
        </button>

        {menu === 'status' && (
          <div className="thread-header__menu" role="menu" data-testid="header-status-menu">
            {/*
              ⛔ THE CLOSE CONFIRM WAS REMOVED ON 2026-08-20 RATHER THAN LEFT
              UNREACHABLE. Its copy read *"Closing adds a line to this
              conversation that the customer can see"*, and Amir's inspection
              reversed exactly that: closing is internal now, so the sentence was
              false and the reason for asking was gone. See `confirmsTransition`.

              ⚠️ A BRANCH KEPT BEHIND A CONDITION THAT IS NOW ALWAYS FALSE is the
              defect this project has already paid for twice - the review
              scaffold and three error states with no producer. Deleting it is
              the cheaper half; `confirmsTransition` still exists and is one line
              to revive if a transition ever becomes irreversible.
            */}
            {transitionsFrom(state.status).map((to) => (
                <button
                  key={to}
                  type="button"
                  role="menuitem"
                  className="thread-header__item"
                  data-testid={`header-status-${to}`}
                  disabled={busy}
                  onClick={() => chooseStatus(to)}
                >
                  {/* ⚠️ `closed→open` IS "Reopen", not "Open". The transition
                      has a name people use and `surfaceFor` already treats it
                      as its own case. */}
                  {state.status === 'closed' && to === 'open' ? 'Reopen' : STATUS_WORD[to]}
              </button>
            ))}
          </div>
        )}

        {state.snoozedUntil !== null && (
          <span className="thread-header__snooze" data-testid="header-snoozed">
            Snoozed
          </span>
        )}
      </div>

      <div className="thread-header__slot">
        <button
          type="button"
          className="thread-header__state"
          data-testid="header-assignee"
          aria-haspopup="menu"
          aria-expanded={menu === 'assignee'}
          disabled={busy}
          onClick={() => toggle('assignee')}
        >
          {assignee.kind === 'assigned' ? assignee.name : 'Unassigned'}
        </button>

        {menu === 'assignee' && (
          <div className="thread-header__menu" role="menu" data-testid="header-assignee-menu">
            {users === null ? (
              <p className="thread-header__loading">Loading</p>
            ) : (
              <>
                {/* ⚠️ UNASSIGN IS AN OPTION, NOT AN OMISSION. Migration 0012:
                    "unassigned is a real and expected state". */}
                <button
                  type="button"
                  role="menuitem"
                  className="thread-header__item"
                  data-testid="header-assignee-none"
                  disabled={busy || assignee.kind === 'unassigned'}
                  onClick={() => void run(() => setAssignee(conversationId, null))}
                >
                  Nobody
                </button>
                {users.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    role="menuitem"
                    className="thread-header__item"
                    data-testid={`header-assignee-${u.id}`}
                    /* ⛔ MATCHED ON THE ID. Two staff called Maria would both
                       highlight if this compared formatted names. */
                    aria-current={assignee.kind === 'assigned' && assignee.userId === u.id}
                    disabled={busy}
                    onClick={() => void run(() => setAssignee(conversationId, u.id))}
                  >
                    {`${u.firstName} ${u.lastName}`.trim()}
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/*
        ⛔ THE SINGLE OVERFLOW. Snooze lives here rather than in its own slot
        because it is an ACTION on the state, and the state slot already shows
        the fact. A fourth visible control is the accretion this header exists
        to refuse.
      */}
      <div className="thread-header__slot thread-header__slot--end">
        <button
          type="button"
          className="thread-header__overflow"
          data-testid="header-overflow"
          aria-haspopup="menu"
          aria-expanded={menu === 'snooze'}
          aria-label="More actions"
          disabled={busy}
          onClick={() => toggle('snooze')}
        >
          {/* ⚠️ THE LITERAL CHARACTER, NOT AN HTML NUMERIC ENTITY. Written as
              an entity, the digits follow a hash and `ops/check-colors.sh`
              reads them as a hex colour - it matches `#` plus hex digits and
              cannot know an entity from a literal. A false positive, and the
              character is the better source anyway.

              ⚠️ AND THIS COMMENT CANNOT SPELL THE ENTITY EITHER, which is the
              same self-matching trap `ops/check-em-dashes.sh` avoids by
              writing its own subject as an escape. */}
          ⋯
        </button>

        {menu === 'snooze' && (
          <div className="thread-header__menu" role="menu" data-testid="header-snooze-menu">
            {/*
              ┌─ ⛔ "MORE ACTIONS" NOW CONTAINS MORE THAN ONE ACTION ────────────┐
              │ Amir's inspection: *"The ... menu carries only snooze. It needs   │
              │ mark answered, mark unanswered, and close."* The button was       │
              │ already labelled "More actions" and opened a single control, so   │
              │ the label was a promise the menu did not keep.                    │
              │                                                                  │
              │ ⚠️ THESE ARE THE SAME TRANSITIONS THE STATUS CONTROL OFFERS, and  │
              │ the duplication is deliberate rather than overlooked. The header  │
              │ makes STATE the control - clicking the word "Open" reveals the    │
              │ transitions - which is discoverable only once somebody has        │
              │ discovered it. An overflow labelled "More actions" is where a     │
              │ person looks first. Both routes call `chooseStatus`, so there is  │
              │ one implementation and one failure line.                          │
              └──────────────────────────────────────────────────────────────────┘
            */}
            {transitionsFrom(state.status).map((to) => (
              <button
                key={`more-${to}`}
                type="button"
                role="menuitem"
                className="thread-header__item"
                data-testid={`header-more-status-${to}`}
                disabled={busy}
                onClick={() => chooseStatus(to)}
              >
                {state.status === 'closed' && to === 'open'
                  ? 'Reopen'
                  : to === 'answered'
                    ? 'Mark Answered'
                    : to === 'open'
                      ? 'Mark Unanswered'
                      : 'Close'}
              </button>
            ))}

            {/* ⚠️ A RULE, NOT A GAP. Without it the transitions and the snooze
                presets read as one undifferentiated list of eight buttons. */}
            <hr className="thread-header__menu-rule" />

            {state.snoozedUntil !== null ? (
              <button
                type="button"
                role="menuitem"
                className="thread-header__item"
                data-testid="header-snooze-cancel"
                disabled={busy}
                onClick={() => void run(() => cancelSnooze(conversationId))}
              >
                Cancel Snooze
              </button>
            ) : (
              PRESETS.map(({ preset, label }) => (
                <button
                  key={preset}
                  type="button"
                  role="menuitem"
                  className="thread-header__item"
                  data-testid={`header-snooze-${preset}`}
                  disabled={busy}
                  onClick={() => void run(() => setSnooze(conversationId, { preset }, null))}
                >
                  {label}
                </button>
              ))
            )}
            {/*
              ⚠️ THE PICKER IS ABSENT AND IT IS BLOCKED, NOT SKIPPED. A calendar
              saying "3pm" must know whose 3pm; that answer lives in
              `src/time/display-timezone.ts`, which now exists, but the input
              itself is this phase's first date control and Amir owns that
              design. Recorded as `date-picker-has-six-consumers-and-no-owner`.
            */}
          </div>
        )}
      </div>

      {failure !== null && (
        <p className="thread-header__failure" role="alert" data-testid="header-failure">
          {failure}
        </p>
      )}
    </div>
  )
}
